import React, { useEffect, useState } from "react"
import { Grid, Box } from "@material-ui/core"
import { Helmet } from "react-helmet"
import firebase from "firebase"
import { useParams } from "react-router-dom"
import styles from "./userpage.module.scss"
import { UserModel } from "models/UserModel"
import { SongModel, SongPrivacyType } from "models/SongModel"
import ReactPlayer from "react-player"
import SongGridItem from "components/SongGridItem/SongGridItem"
import { logEvent } from "analyticsEvents"

interface Props {
  userHandle: string
}

const UserPage = () => {
  const { userHandle } = useParams<Props>()
  const [user, setUser] = useState<UserModel>()
  const [userNumListens, setUserNumListens] = useState<number>()
  const [songs, setSongs] = useState<SongModel[]>([])
  const [songPlayUrl, setSongPlayUrl] = useState<string>("")
  const [playingSongId, setPlayingSongId] = useState<string|null>(null)
  const [profilePicUrl, setProfilePicUrl] = useState<string>()
  const [followerUserIds, setFollowerUserIds] = useState<string[]>([])
  const [fontsLoaded, setFontsLoaded] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<boolean>(false)
  const db = firebase.firestore()

  const fetchData = async (userHandle: string) => {
    try {
      const userSnapshot = await db.collection("users")
        .where("normalizedUserHandle", "==", userHandle.toLocaleLowerCase())
        .limit(1)
        .get()
      if (userSnapshot.empty) {
        setError(true);
        console.error("No such user!");
        return;
      }
      const user = userSnapshot.docs[0].data() as UserModel;
      setUser(user);
      await Promise.all([
        await fetchUserProfilePic(user.uid),
        await fetchThreeFollowers(user.uid),
        await fetchSongsForUser(user.uid, user.pinnedSongIds || []),
        await fetchUserNumListens(user.uid)
      ]);
    } catch (error) {
      setError(true)
      console.log("Error getting user:", error)
    }
  }

  const fetchUserProfilePic = async (uid: string) => {
    const profilePicUrl = await firebase
      .storage()
      .ref(`profiles/${uid}`)
      .getDownloadURL();
    setProfilePicUrl(profilePicUrl);
  }

  const fetchSongsForUser = async (uid: string, pinnedSongIds: string[]) => {
    const songSnapshotPromise = db.collection("songs")
      .where("creatorID", "==", uid)
      .where("songType", "==", SongPrivacyType.forPublic)
      .orderBy("allTimeScore", "desc")
      .limit(9)
      .get()
    const pinnedSnapshotPromise = pinnedSongIds.length == 0 ? Promise.resolve(null) :
        db.collection("songs").doc(pinnedSongIds[0]).get();
    const [songSnapshot, pinnedSnapshot] = await Promise.all([songSnapshotPromise, pinnedSnapshotPromise]);
    if (songSnapshot.empty) {
      return;
    }

    const songs = songSnapshot.docs
      .map((doc) => doc.data() as SongModel)
      .filter((song) => !pinnedSongIds.includes(song.songID));
    if (pinnedSnapshot && pinnedSnapshot.exists) {
      songs.unshift(pinnedSnapshot.data() as SongModel);
    }

    const songUrl = await firebase
      .storage()
      .ref(`songs/${songs[0].songID}`)
      .getDownloadURL();
    setSongPlayUrl(songUrl);
    setSongs(songs);
  }

  const fetchUserNumListens = async (uid: string) => {
    const songBatchSize = 300;
    let totalNumListens = 0;
    let lastDate = firebase.firestore.Timestamp.fromMillis(0);
    while (true) {
      const songSnapshot = await db.collection("songs")
        .where("creatorID", "==", uid)
        .orderBy("creationTime", "asc")
        .limit(songBatchSize)
        .startAfter(lastDate)
        .get();

      totalNumListens = songSnapshot.docs
        .reduce((numListens, doc) => {
          const song = doc.data() as SongModel;
          return numListens + (song.numListens || 0)
        }, totalNumListens);

      if (songSnapshot.size < songBatchSize) {
        setUserNumListens(totalNumListens);
        return;
      }
      const lastDateSong  = songSnapshot.docs[songSnapshot.size - 1].data() as SongModel;
      lastDate = lastDateSong.creationTime;
    }
  }

  const fetchThreeFollowers = async (uid: string) => {
    const randomThreeFollowersSnapshot = await db.collection("users").doc(uid).collection("followers").limit(3).get();
    setFollowerUserIds(randomThreeFollowersSnapshot.docs.map((doc) => doc.id));
  }

  useEffect(() => {
    fetchData(userHandle);
  }, [userHandle])

  useEffect(() => {
    // @ts-ignore
    document.fonts.ready.then(() => {
      setFontsLoaded(true);
    });
  }, [])

  const onSongTapped = async (songId: string, userId: string, index: number) => {
    if (playingSongId == songId) {
      setPlayingSongId(null);
      logEvent('song_paused', { song_id: songId, user_id: userId, index });
    } else {
      const songUrl = `https://firebasestorage.googleapis.com/v0/b/beastdrop.appspot.com/o/songs%2F${songId}?alt=media`
      setSongPlayUrl(songUrl);
      setPlayingSongId(songId);
      logEvent('song_played', { song_id: songId, user_id: userId, index });
    }
  }

  useEffect(() => {
    if (user && profilePicUrl && fontsLoaded) {
      setLoading(false)
    }
  }, [user, profilePicUrl, fontsLoaded])

  let bodyContent = <div className={styles.loadingPlaceholder}></div>
  if (error) {
    bodyContent = errorContent();
  } else if (!loading) {
    bodyContent = userMetadata(user!, userNumListens, profilePicUrl!, playingSongId, onSongTapped, songs, followerUserIds, songPlayUrl);
  }

  return (
    <>
      <Helmet>
        <title>Mayk{ !!userHandle ? ` - @${userHandle}` : "" }</title>
      </Helmet>
      <Box
        alignItems='center'
        flexDirection='column'
        display='flex'
        className={styles.grid}
      >
        <div className={styles.backgroundOverlay} style={{ background: `repeat-y center/cover url(${profilePicUrl})` }} />
        <div className={styles.backgroundOverlayBlur} />
        <Box className={styles.rootCard}>
          {bodyContent}
        </Box>
      </Box>
    </>
  )
}

const userMetadata = (
  user: UserModel,
  userNumListens: number|undefined,
  profilePicUrl: string,
  playingSongId: string|null,
  onSongTapped: (songId: string, userId: string, index: number) => Promise<void>,
  songs: SongModel[],
  followerUserIds: string[],
  songUrl?: string,
) => {
  return (
    <>
      <div className={styles.metadataContainer}>
        <Box display="flex" alignItems="center" className={styles.profilePicAndStatsContainer}>
          <img src={profilePicUrl} className={styles.userProfileImage} />
          <div className={styles.userMetadata}>
            <div className={styles.userName}>
              {user.name}
              <img hidden={!user.isVerified} src={require("../../resources/verified.svg").default} className={styles.verifiedIcon} />
            </div>
            <div className={styles.userHandle}>@{user.userHandle}</div>
            <Box display="flex">
              <span className={styles.userStats + (userNumListens === undefined ? ` ${styles.loadingListens}` : '')}>{formatListenCount(userNumListens)}</span>
              <div className={styles.statsSeparator} />
              <div className={styles.followerStatsWrapper}>
                {followerProfileImages(followerUserIds)}
                <span className={styles.userStats}>{user.numFollowers} fans</span>
              </div>
            </Box>
          </div>
        </Box>
        <div className={styles.userBio}>{user.userBio || "MAYKING DOPE TRACKS"}</div>
        <Box display="flex" flexDirection="row" alignItems="center" gridGap="32px">
          <a className={styles.metadataButton}
              onClick={() => logEvent('follow_clicked', { user_id: user.uid })}
              href="https://link.mayk.it/rdYp/userProfileWebFollow"
              target="_blank">
            <span className={styles.rainbowText}>Follow</span>
          </a>
        </Box>
        <div className={styles.separator}></div>
        {songGrid(songs, user.pinnedSongIds || [], playingSongId, onSongTapped)}
        <ReactPlayer url={songUrl} loop playing={playingSongId !== null} width={0} height={0} config={{
          file: {
            forceAudio: true,
          },
        }} />
      </div>
      {seeMoreInAppToast(user.name, user.uid)}
    </>
  )
}

const followerProfileImages = (threeFollowerIDs: string[]) => {
  const followerCircles = [0, 1, 2].map((index) => {
    let imgEl = <></>
    if (threeFollowerIDs.length > index) {
      const profileUrl = `https://firebasestorage.googleapis.com/v0/b/beastdrop.appspot.com/o/profiles%2F${threeFollowerIDs[index]}?alt=media`
      imgEl = <img src={profileUrl} alt="follower profile image" />
    }
    return (
      <div key={index} className={styles.followerImage}>{imgEl}</div>
    )
  });
  return (
    <div className={styles.followerImageWrapper}>
      {followerCircles}
    </div>
  )
}

const formatListenCount = (numListens: number|undefined) => {
  if (!numListens) {
    return "..... listens"
  }

  if (numListens < 1000) {
    return `${numListens} listens`;
  }

  if (numListens < 1000000) {
    return `${(numListens / 1000).toFixed(1)}K listens`;
  }

  return `${(numListens / 1000000).toFixed(2)}M listens`;
};

const songGrid = (
  songs: SongModel[],
  pinnedIds: string[],
  playingSongID: string|null,
  onSongTapped: (songId: string, userId: string, index: number) => Promise<void>
) => {
  const items = songs.map((song, index) =>
    <Grid item xs={6} md={4} key={song.songID}>
      <SongGridItem
        title={song.title}
        songArtworkUrl={song.gifURL || ""}
        numPlays={song.numListens}
        disabled={index > 1}
        playing={playingSongID === song.songID}
        onClick={index <= 1 ? () => {
          onSongTapped(song.songID, song.creatorID, index)
        } : () => {
          logEvent('locked_song_clicked', { song_id: song.songID, user_id: song.creatorID, index });
          window.open('https://link.mayk.it/rdYp/userProfileWebLockedSong', '_blank')
        }}
        pinned={pinnedIds.includes(song.songID)} />
    </Grid>
  );
  return (
    <Grid container spacing={1}>
      {items}
    </Grid>
  )
}

const errorContent = () => {
  return (
    <Box className={styles.errorContent}>
      <img
        className={styles.errorIcon}
        src={require("../../resources/error.svg").default}
      />
      <p className={styles.errorText}>Couldn't find that artist...</p>
    </Box>
  )
};

const onSeeMoreClicked = (userId: string) => {
  logEvent('see_more_clicked', { user_id: userId });
};

const seeMoreInAppToast = (artistName: string, userId: string) => {
  return (
    <div className={styles.seeMoreToast}>
        <img src={process.env.PUBLIC_URL + "/logo256.png"} alt='logo' className={styles.maykLogo} />
        <p className={styles.seeMoreToastText}>Listen to more of {artistName}'s songs on mayk.it</p>
        <a className={styles.seeMoreToastButton}
            onClick={() => onSeeMoreClicked(userId)}
            href="https://link.mayk.it/rdYp/userProfileWebFollow"
            target="_blank">view</a>
    </div>
  )
};

export default UserPage
